/**
 * Strata Relay — browser alerts for new messages.
 *
 * When a `message.new` arrives over the shared SSE stream (see
 * RelayUnreadContext) and it's NOT your own message and you're NOT
 * already looking at that room, we:
 *   • play a subtle two-note chime (Web Audio — no asset file), and
 *   • pop a native OS notification when the tab is backgrounded, or
 *     an in-app toast when the tab is focused (but on another room).
 *
 * A per-browser mute flag (localStorage) gates both, toggled from the
 * bell button in the Relay page header.
 */
import { toast } from 'sonner';

const MUTE_KEY = 'sx_relay_alerts_muted';
let audioCtx = null;
let unlockBound = false;
const muteListeners = new Set();

// ── Mute state (per browser) ───────────────────────────────────────
export function isRelayMuted() {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setRelayMuted(muted) {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* storage off */ }
  muteListeners.forEach((fn) => { try { fn(muted); } catch { /* isolate */ } });
}

export function subscribeMute(fn) {
  muteListeners.add(fn);
  return () => muteListeners.delete(fn);
}

// ── Audio (Web Audio API — no asset) ───────────────────────────────
function getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  return audioCtx;
}

/** Browsers suspend audio until a user gesture — resume the context on
 * the first interaction so the very first chime is audible. */
export function initRelayAudioUnlock() {
  if (unlockBound || typeof window === 'undefined') return;
  unlockBound = true;
  const unlock = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === 'suspended') { ctx.resume().catch(() => {}); }
  };
  window.addEventListener('pointerdown', unlock, { once: false, passive: true });
  window.addEventListener('keydown', unlock, { once: false });
}

export function playChime() {
  if (isRelayMuted()) return;
  try {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    // Two soft sine notes: A5 → E6, quick decay.
    [{ f: 880, t: 0 }, { f: 1318.5, t: 0.11 }].forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      const start = now + t;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.32);
    });
  } catch { /* audio unavailable */ }
}

// ── Per-room notify tier (LOOP W4) ─────────────────────────────────
// RelayPage feeds these from each room payload's `notify_level` so the web
// chime/toast/OS-notification honor the SAME tier as mobile push.
// { roomId: { level: 'all'|'mentions'|'none', dm: bool } }
let roomTiers = {};

export function setRoomTiers(map) {
  roomTiers = map || {};
}

export function setRoomTier(roomId, level, dm) {
  if (!roomId) return;
  roomTiers = { ...roomTiers, [roomId]: { level: level || 'all', dm: !!dm } };
}

/** Web approximation of the tier rules: 'all' always, 'none' never, 'mentions'
 * only on @mention or DM. Thread-participation is intentionally handled only by
 * the authoritative server-side push (the client can't know thread authors). */
function shouldAlertForRoom(roomId, mentioned) {
  const t = roomTiers[roomId];
  const level = (t && t.level) || 'all';
  if (level === 'none') return false;
  if (level === 'all') return true;
  return !!mentioned || !!(t && t.dm);
}

// ── Native notifications ───────────────────────────────────────────
export function notificationPermission() {
  return (typeof Notification !== 'undefined') ? Notification.permission : 'unsupported';
}

export async function ensureNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'default') {
    try { return await Notification.requestPermission(); } catch { return Notification.permission; }
  }
  return Notification.permission;
}

function showBrowserNotification({ title, body, tag, onClick }) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag, icon: '/favicon-192.png', renotify: true });
    n.onclick = () => {
      try { window.focus(); } catch { /* noop */ }
      if (onClick) onClick();
      n.close();
    };
  } catch { /* construction blocked */ }
}

// ── Preview text ───────────────────────────────────────────────────
function previewText(msg) {
  const body = (msg.body || '').trim();
  if (body) return body.length > 140 ? `${body.slice(0, 140)}…` : body;
  if ((msg.attachments || []).length) return 'Sent an attachment';
  return 'New message';
}

/**
 * Decide + fire the alert for an incoming `message.new` event.
 * @param evt  the SSE event: { type, room_id, message: {author_id, author_name, body, attachments} }
 * @param opts { myId, activeRoomId, navigate }
 */
export function handleIncomingRelayMessage(evt, { myId, activeRoomId, navigate } = {}) {
  const msg = evt?.message || {};
  if (!msg.author_id) return;
  if (String(msg.author_id) === String(myId)) return;   // never alert on own messages
  if (isRelayMuted()) return;

  const focused = document.visibilityState === 'visible' && document.hasFocus();
  // Actively viewing this room → stay quiet, the message just appears.
  if (focused && activeRoomId && evt.room_id === activeRoomId) return;

  // LOOP W4 — honor the room's notify tier (same rules as mobile push).
  const mentioned = Array.isArray(msg.mentions) && myId != null
    && msg.mentions.map(String).includes(String(myId));
  if (!shouldAlertForRoom(evt.room_id, mentioned)) return;

  const sender = msg.author_name || 'New message';
  const preview = previewText(msg);
  const open = () => { try { navigate?.(`/relay?room=${encodeURIComponent(evt.room_id || '')}`); } catch { /* noop */ } };

  playChime();

  if (!focused) {
    showBrowserNotification({ title: sender, body: preview, tag: `relay-${evt.room_id}`, onClick: open });
  } else {
    toast(sender, {
      description: preview,
      action: { label: 'Open', onClick: open },
    });
  }
}
